<template>
  <div
    class="ai-chat-input"
    :class="{ 'ai-chat-input--dragover': isDragover }"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="filePicker"
      type="file"
      class="d-none"
      multiple
      accept="image/*,.psd"
      @change="onAttachFiles"
    >
    <div class="ai-chat-input__editor">
      <div
        v-if="files.length"
        class="ai-chat-input__preview"
      >
        <div
          v-for="(file, index) in files"
          :key="index"
          class="ai-chat-input__item"
        >
          <img
            v-if="isImage(file)"
            :src="file.preview"
            class="ai-chat-input__item--image"
            alt="preview-img"
          >
          <div
            v-else
            class="ai-chat-input__item--file"
          >
            {{ truncateFileName(file.name) }}
          </div>
          <button
            class="ai-chat-input__item--remove"
            @click.prevent="removeFile(index)"
          >
            <i class="bi-x" />
          </button>
        </div>
      </div>
      <div class="ai-chat-input__editor-prompt-area">
        <div class="ai-chat-input__icons">
          <button
            type="button"
            class="ai-chat-input__icon-btn"
            title="Upload from desktop"
            :disabled="disabled"
            @click="openFilePicker"
          >
            <i class="bi bi-paperclip" />
          </button>
          <button
            type="button"
            class="ai-chat-input__icon-btn"
            title="Select from folders"
            :disabled="disabled"
            @click="openFileBrowser"
          >
            <i class="bi bi-folder" />
          </button>
          <ClipboardQuickInsertPopover
            trigger-class="ai-chat-input__icon-btn"
            :disabled="disabled"
            @select="onClipboardQuickInsert"
          />
        </div>
        <div
          ref="editorPrompt"
          class="ai-chat-input__editor-prompt"
          :contenteditable="!disabled"
          :data-placeholder="!prompt.length ? 'Ask Optix AI...' : ''"
          @input="onEditorInput" 
          @keydown.stop="handleKeydown"
          @paste.stop="handlePaste"
        />
        <b-button
          class="ai-chat-input__send"
          variant="primary"
          size="sm"
          :disabled="disabled || prompt.length === 0"
          @click="sendMessage"
        >
          <i class="cil-send" />
        </b-button>
      </div>
    </div>
    <div
      v-if="isDragover"
      class="ai-chat-input__overlay"
    />
  </div>
</template>

<script>
import ClipboardQuickInsertPopover from '@frontend/components/common/clipboard/ClipboardQuickInsertPopover.vue';

export default {
  name: 'AIChatTextArea',

  components: {
    ClipboardQuickInsertPopover,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      prompt: '',
      files: [],
      isDragover: false
    }
  },
  mounted() {
    this.clearEditor();
  },
  methods: {
    setSuggestion(text) {
      this.prompt = text;
      this.$refs.editorPrompt.innerText = text;
      this.$emit('update:prompt', text);
      this.$nextTick(() => this.$refs.editorPrompt.focus());
    },
    onClipboardQuickInsert(item) {
      const content = String(item?.content || '').trim();
      if (!content) {
        return;
      }
      this.setSuggestion(content);
    },
    onDrop(event) {
      this.isDragover = false;
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },
    onAttachFiles(e) {
      const files = Array.from(e.target.files || []);
      if (files.length) {
        this.addFiles(files);
      }
      e.target.value = '';
    },
    openFilePicker() {
      if (this.disabled) return;
      this.$refs.filePicker?.click?.();
    },
    openFileBrowser() {
      if (this.disabled) return;
      this.$bvModal?.show?.('select-folder-modal');
    },
    async fetchFilesFromBrowserSelection(items) {
      const results = [];

      for (const item of items) {
        const name =
          item?.name
          || (item?.original_path ? String(item.original_path).split('/').pop() : null)
          || (item?.path ? String(item.path).split('/').pop() : null)
          || 'file';
        const url = item?.url || (item?.path ? `/share?file=${item.path}` : null);
        if (!url) continue;

        try {
          const response = await fetch(url);
          if (!response.ok) continue;
          const blob = await response.blob();
          const contentType = response.headers.get('content-type') || '';
          const mime = blob.type || contentType || this.guessMimeType(name);
          const finalName = this.ensureExtension(name, mime);
          results.push(new File([blob], finalName, { type: mime }));
        } catch (e) {
          console.error('Failed to fetchFilesFromBrowserSelection', e);
        }
      }

      return results;
    },
    guessMimeType(filename) {
      const ext = (filename.split('.').pop() || '').toLowerCase();
      const map = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        bmp: 'image/bmp',
        svg: 'image/svg+xml',
        psd: 'image/vnd.adobe.photoshop',
      };
      return map[ext] || 'application/octet-stream';
    },
    onDragOver() {
      this.isDragover = true;
    },
    onDragLeave(e) {
      if (!this.$el.contains(e.relatedTarget)) {
        this.isDragover = false;
      }
    },
    handleKeydown(e) {
      if (this.disabled) {
        e.preventDefault();
        return;
      }
      
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
        return;
      }
    },
    addFiles(newFiles) {
      const mapped = newFiles.map(file => {
        const isImage = (file.type && file.type.startsWith('image/')) || this.isImageByName(file?.name)

        return {
          file,
          name: file.name || `pasted-image-${Date.now()}.png`,
          type: file.type,
          payload: file,
          preview: isImage
            ? URL.createObjectURL(file)
            : null
        }
      })

      this.files = [...this.files, ...mapped];
      this.$emit('update:files', this.files);
    },
    removeFile(index) {
      const file = this.files[index];

      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }

      this.files.splice(index, 1);
      this.$emit('update:files', this.files);
    },
    isImage(file) {
      if (!file) return false;
      const mime = file.type || file.payload?.type || '';
      if (mime && String(mime).startsWith('image/')) {
        return true;
      }

      const name = file.name || '';
      const ext = name.split('.').pop().toLowerCase();

      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

      return imageExts.includes(ext);
    },
    isImageByName(name) {
      if (!name) return false;
      const ext = String(name).split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    },
    ensureExtension(name, mime) {
      if (!name) return name;
      const hasExt = /\.[A-Za-z0-9]{2,5}$/.test(name);
      if (hasExt) return name;

      const ext = this.mimeToExt(mime);
      return ext ? `${name}.${ext}` : name;
    },
    mimeToExt(mime) {
      const m = String(mime || '').toLowerCase();
      if (m.includes('image/jpeg')) return 'jpg';
      if (m.includes('image/jpg')) return 'jpg';
      if (m.includes('image/png')) return 'png';
      if (m.includes('image/gif')) return 'gif';
      if (m.includes('image/webp')) return 'webp';
      if (m.includes('image/bmp')) return 'bmp';
      if (m.includes('image/svg')) return 'svg';
      if (m.includes('photoshop') || m.includes('psd')) return 'psd';
      return '';
    },
    onEditorInput() {
      if (this.disabled) {
        return;
      }
      
      const text = this.getText();
      this.prompt = text;
      this.$emit('update:prompt', text);
    },
    handlePaste(e) {
      if (this.disabled) {
        e.preventDefault();
        return;
      }
      
      const items = (e.clipboardData || window.clipboardData).items;

      let hasFile = false;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.kind === 'file') {
          const file = item.getAsFile();

          if (file) {
            hasFile = true;
            this.addFiles([file]);
          }
        }
      }

      if (hasFile) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      const text = (e.clipboardData || window.clipboardData).getData('text');
      this.insertTextAtCursor(text);
    },
    insertTextAtCursor(text) {
      const selection = window.getSelection()

      if (!selection.rangeCount) {
        return;
      }

      const range = selection.getRangeAt(0);

      range.deleteContents();

      const textNode = document.createTextNode(text);
      range.insertNode(textNode);

      range.setStartAfter(textNode);
      range.setEndAfter(textNode);

      selection.removeAllRanges();
      selection.addRange(range);

      this.onEditorInput();
    },
    getText() {
      return this.$refs.editorPrompt.innerText
        .replace(/\n\n+/g, '\n')
        .trim();
    },
    clearEditor() {
      this.$refs.editorPrompt.innerHTML = '<p><br></p>'
    },
    sendMessage() {
      if (!this.prompt && !this.files.length) return

      this.$emit('send-message', {
        text: this.prompt,
        files: this.files
      })

      this.prompt = ''
      this.files = []
      this.clearEditor()

      this.$nextTick(() => {
        this.$refs.editorPrompt.focus()
      })
    },
    truncateFileName(name, maxLength = 20) {
      if (!name) {
        return '';
      }

      const extIndex = name.lastIndexOf('.');
      if (extIndex === -1) {
        return name.length > maxLength
          ? name.slice(0, maxLength) + '...'
          : name
      }

      const ext = name.slice(extIndex);
      const base = name.slice(0, extIndex);

      const availableLength = maxLength - ext.length - 3;

      if (base.length <= availableLength) {
        return name;
      }

      return base.slice(0, availableLength) + '...' + ext;
    }
  },
}
</script>

<style scoped lang="scss">
.ai-chat-input {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px;
  transition: border-color 0.2s;
  background: #fff;

  &--dragover {
    border-color: #007bff;
    background: rgba(174, 174, 174, 0.24);
    border-radius: 10px;
    user-select: none;
  }

  &__preview {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  &__item {
    position: relative;
    width: 60px;
    height: 60px;
    background: #f3f3f3;
    font-size: 10px;
    color: #333;
    text-align: center;
    border-radius: 6px;
    border: 1px solid #ccc;

    &--image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 6px;
    }

    &--file {
      font-size: 10px !important;
      background: #f3f3f3;
      padding: 4px;
      border-radius: 6px;
      height: 100%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 0.5rem !important;
      word-break: break-all;
    }

    &--remove {
      position: absolute;
      top: -6px;
      right: -6px;
      background: #000;
      color: #fff;
      border: none;
      border-radius: 50%;
      width: 16px;
      height: 16px;
      font-size: 10px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }
  }

  &__editor {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    border: 1px solid #6EBFB0;
    border-radius: 10px;
    align-items: stretch;

    &-prompt-area {
      display: flex;
      gap: 6px;
      align-items: flex-end;
      justify-content: flex-start;
      width: 100%;
      min-width: 0;
    }
  }

  &__editor-prompt {
    flex: 1 1 auto;
    min-width: 0;
    padding: 5px 0;
    max-height: 40vh;
    overflow-y: auto;
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: anywhere;
    outline: none;
    transition: all 0.15s ease;
    position: relative;

    &::before {
      content: attr(data-placeholder);
      position: absolute;
      top: 5px;
      color: #aaa;
      pointer-events: none;
      display: block;
    }

    &::v-deep p {
      margin: 0;
    }
  }

  &__send {
    width: 30px;
    min-width: 30px;
    height: 30px;
    padding: 0;
    border-radius: 6px;
    flex-shrink: 0;
    align-self: flex-end;
  }

  &__icons {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    align-self: flex-end;
    padding-bottom: 1px;
    gap: 0;

    > * + * {
      margin-left: -3px;
    }
  }

  &__icon-btn,
  &__icons ::v-deep .ai-chat-input__icon-btn {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: transparent;
    color: #6c757d;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #6EBFB0;
      color: #fff;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 123, 255, 0.08);
    border: 2px dashed #209c86;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }
}
</style>
